import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Syringe, ShieldCheck, Activity, ClipboardCheck, Dna, TrendingUp, CheckCircle2, ArrowRight } from 'lucide-react';
import { CLINIC, IMAGES } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

const TYPES = [
  { icon: Dna, title: 'TNF Inhibitors', body: 'Adalimumab, Etanercept, Infliximab — highly effective for RA, AS and psoriatic arthritis.' },
  { icon: ShieldCheck, title: 'IL-17 / IL-23 Inhibitors', body: 'Secukinumab, Ustekinumab — targeted therapy for spondyloarthritis and psoriasis.' },
  { icon: Activity, title: 'B-cell Therapy', body: 'Rituximab — used in RA, vasculitis and refractory autoimmune disease.' },
  { icon: TrendingUp, title: 'IL-6 Inhibitors', body: 'Tocilizumab — for RA, giant cell arteritis and adult-onset Still’s disease.' },
];

const STEPS = [
  { n: '01', title: 'Assessment & Screening', body: 'Detailed evaluation with infection screening (TB, hepatitis) before starting therapy.' },
  { n: '02', title: 'Personalised Selection', body: 'The right biologic is chosen based on your diagnosis, disease activity and comorbidities.' },
  { n: '03', title: 'Guided Initiation', body: 'Careful initiation with education on administration and what to expect.' },
  { n: '04', title: 'Ongoing Monitoring', body: 'Regular reviews and blood tests to keep treatment safe and effective.' },
];

export default function BiologicalTherapy() {
  return (
    <div>
      <PageHero eyebrow="Advanced Treatment" title="Biological therapy for autoimmune arthritis" subtitle="Modern, targeted medicines that have transformed outcomes for inflammatory and autoimmune conditions — delivered under expert specialist supervision." image={IMAGES.injection} />

      <section className="py-20">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.injection} alt="Biologic therapy" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionTitle eyebrow="What are biologics?" title="Precision medicine for the immune system" />
            <p className="text-slate-600 leading-relaxed mb-4">Biologic therapies are advanced medicines that target specific parts of the immune system responsible for inflammation — rather than suppressing it broadly. This makes them highly effective at controlling disease activity and preventing long-term joint damage.</p>
            <p className="text-slate-600 leading-relaxed mb-6">They are used when conventional DMARDs are not enough, and are prescribed and monitored by an experienced rheumatologist.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {['Slows disease progression', 'Reduces pain & swelling', 'Prevents joint damage', 'Improves quality of life'].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-700"><CheckCircle2 size={17} className="text-teal-600" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mint-section py-20">
        <div className="container-x">
          <SectionTitle center eyebrow="Therapies Offered" title="Types of biologic therapy" sub="We offer the full range of modern biologic and targeted synthetic therapies." />
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {TYPES.map((t, i) => (
              <Reveal key={t.title} delay={i * 80}>
                <div className="card-lift bg-white border border-teal-50 rounded-2xl p-7 h-full">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl text-white mb-5" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}><t.icon size={26} /></span>
                  <h3 className="text-lg text-teal-900 mb-2" style={{ fontWeight: 600 }}>{t.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{t.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow="Our Process" title="Safe, structured & closely monitored" />
          <div className="grid md:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 80}>
                <div className="relative bg-white border border-teal-50 rounded-2xl p-7 h-full card-lift">
                  <span className="font-display text-4xl" style={{ color: 'var(--teal-100)', fontWeight: 700 }}>{s.n}</span>
                  <h3 className="text-lg text-teal-900 mt-2 mb-2" style={{ fontWeight: 600 }}>{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="deep-section relative overflow-hidden">
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blob" style={{ background: 'rgba(20,184,166,0.15)' }} />
        <div className="container-x py-14 relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/10 text-white"><Syringe size={26} /></span>
            <h2 className="text-white text-2xl md:text-3xl text-balance" style={{ fontWeight: 600 }}>Considering biologic therapy? Talk to a specialist.</h2>
          </div>
          <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold shrink-0"><Phone size={18} /> {CLINIC.phone}</a>
        </div>
      </section>
    </div>
  );
}
